import React from "react";
import {
  Check, X, Truck, PackageCheck, Ban, Eye, Pencil, Trash2, MoreHorizontal, PlayCircle, Wrench, Factory, FileCheck2, Coins, IndianRupee,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatINR } from "@/lib/api";
import { SIMPLE_STATUS_LABELS, SIMPLE_STATUS_STYLE, PAYMENT_STYLE, PAYMENT_LABELS, fmtGrams } from "@/pages/admin/orders/orderConstants";

// The "Orders" tab: only Pending / In Progress / Ready orders that have NOT been invoiced
// yet — simplified column set per the business spec (no Invoice No/GSTIN/tax columns,
// those live in the Invoices tab). "Payment" here treats gold received as payment too,
// not just rupees, since a customer can partially settle an order by sending metal before
// the formal invoice is even generated.
export const OrdersTable = ({
  orders, ordersLoading, selectedIds, toggleSelect, toggleSelectAll, allSelected, canAct, busy, staff,
  navigate, onApprove, onProcess, onCancel, onReject, onShip, onView, onEditItems, onDelete, onGenerateInvoice,
}) => (
  <Card className="overflow-hidden">
    <div className="overflow-x-auto">
      <Table data-testid="admin-orders-table">
        <TableHeader><TableRow className="bg-[#F4EBDD]">
          <TableHead className="w-10"><Checkbox data-testid="select-all-orders" checked={allSelected} onCheckedChange={toggleSelectAll} /></TableHead>
          <TableHead>Order Date</TableHead><TableHead>Order No</TableHead><TableHead>Customer</TableHead>
          <TableHead>Mobile</TableHead><TableHead>Salesman</TableHead>
          <TableHead className="text-right">Total</TableHead>
          <TableHead>Order Status</TableHead><TableHead>Payment</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {ordersLoading ? (
            <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
          ) : orders.length === 0 ? (
            <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground" data-testid="orders-empty-state">No orders in Pending, In Progress or Ready right now.</TableCell></TableRow>
          ) : orders.map((o) => {
            const allowed = canAct(o);
            const payKey = o.combined_payment_status || "not_invoiced";
            const goldPaid = Number(o.gold_paid_fine_g || 0);
            const cashPaid = Number(o.amount_paid || 0);
            return (
              <TableRow key={o.id} className="hover:bg-[#F7F0E6]" data-testid="order-row">
                <TableCell><Checkbox data-testid="select-order-checkbox" checked={selectedIds.includes(o.id)} onCheckedChange={() => toggleSelect(o.id)} /></TableCell>
                <TableCell className="text-xs">{new Date(o.created_at).toLocaleDateString("en-IN")}</TableCell>
                <TableCell className="font-mono text-xs">
                  <div className="flex items-center gap-1.5">
                    {o.order_no}
                    {o.has_production_items && (
                      <span data-testid="order-production-flag" title="Includes Make to Order item(s)">
                        <Factory className="h-3.5 w-3.5 text-[hsl(var(--gold-dark))]" />
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-sm max-w-[140px] truncate">{o.shop_name}</TableCell>
                <TableCell className="text-xs font-mono">{o.shop_phone}</TableCell>
                <TableCell className="text-xs">{o.salesman_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                <TableCell className="text-right font-mono text-sm">{formatINR(o.total)}</TableCell>
                <TableCell><Badge data-testid="order-status-badge" className={`${SIMPLE_STATUS_STYLE[o.status]} text-xs`}>{SIMPLE_STATUS_LABELS[o.status] || o.status}</Badge></TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <Badge data-testid="order-payment-badge" className={`${PAYMENT_STYLE[payKey] || ""} text-xs w-fit`}>{PAYMENT_LABELS[payKey] || payKey}</Badge>
                    {(cashPaid > 0 || goldPaid > 0) && (
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        {cashPaid > 0 && <span className="flex items-center gap-0.5" title="Cash received"><IndianRupee className="h-2.5 w-2.5" />{formatINR(cashPaid)}</span>}
                        {goldPaid > 0 && <span className="flex items-center gap-0.5" title="Gold received"><Coins className="h-2.5 w-2.5" />{fmtGrams(goldPaid)}</span>}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    {o.status === "pending_approval" && allowed && (
                      <>
                        <Button data-testid="order-approve-button" size="sm" variant="secondary" className="gap-1 h-7 px-2" disabled={busy} onClick={() => onApprove(o)}><Check className="h-3.5 w-3.5" /></Button>
                        <Button data-testid="order-reject-button" size="sm" variant="ghost" className="gap-1 h-7 px-2" disabled={busy} onClick={() => onReject(o)}><X className="h-3.5 w-3.5" /></Button>
                      </>
                    )}
                    {o.status === "approved" && allowed && (
                      <Button data-testid="order-process-button" size="sm" variant="secondary" className="gap-1 h-7 px-2" disabled={busy} onClick={() => onProcess(o)}><PlayCircle className="h-3.5 w-3.5" /></Button>
                    )}
                    {o.status === "processing" && allowed && (
                      <Button data-testid="order-ship-button" size="sm" variant="secondary" className="gap-1 h-7 px-2" disabled={busy} onClick={() => onShip(o)}><Truck className="h-3.5 w-3.5" /></Button>
                    )}
                    {allowed && (
                      <Button data-testid="order-generate-invoice-button" size="sm" className="gap-1.5 h-7 px-2.5" disabled={busy} onClick={() => onGenerateInvoice(o)}>
                        <FileCheck2 className="h-3.5 w-3.5" /> Generate Invoice
                      </Button>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button data-testid="order-actions-menu" size="icon" variant="ghost" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid="action-view-order" onClick={() => onView(o)}><Eye className="h-3.5 w-3.5 mr-2" /> View Order</DropdownMenuItem>
                        {o.status === "pending_approval" && allowed && (
                          <DropdownMenuItem data-testid="action-edit-order" onClick={() => onEditItems(o)}><Wrench className="h-3.5 w-3.5 mr-2" /> Edit Order</DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {allowed && (
                          <DropdownMenuItem data-testid="action-cancel-invoice" onClick={() => onCancel(o)} className="text-destructive"><Ban className="h-3.5 w-3.5 mr-2" /> Cancel Order</DropdownMenuItem>
                        )}
                        {staff?.is_manager && (
                          <DropdownMenuItem data-testid="action-delete-invoice" onClick={() => onDelete(o)} className="text-destructive"><Trash2 className="h-3.5 w-3.5 mr-2" /> Delete Order</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  </Card>
);
